import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import cookie from "react-cookies";
import {
  completeClipUpload,
  completeDeliverableUpload,
  getMyCreatorSubmission,
  putFileWithProgress,
  requestClipUploadUrl,
  requestDeliverableUploadUrl,
  submitCreatorSubmission,
} from "../../../services/creatorApi";
import { TrainingLoading } from "../../academy/AcademyShared";
import {
  CreatorLayout,
  CREATOR_CRUMBS,
  CreatorStatusChip,
  CreatorDecisionBanner,
  formatClipCue,
} from "./CreatorShared";
import { handleCreatorGateError } from "./creatorLicense";
import {
  getSubmissionProgress,
  groupV2Deliverables,
  isV2Brief,
} from "./creatorModels";

const MAX_BYTES = 200 * 1024 * 1024;

function clipUiState(clip, local) {
  if (local?.phase) return local;
  if (!clip?.has_file) return { phase: "empty" };
  if (clip.status === "needs_retake") {
    return { phase: "needs_retake", note: clip.retake_note };
  }
  if (clip.status === "accepted") return { phase: "accepted" };
  return { phase: "uploaded" };
}

export default function CreatorUploadWorkspace() {
  const { submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [localClips, setLocalClips] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const cancelRef = useRef({});
  const previewRef = useRef({});

  const load = useCallback(async () => {
    const res = await getMyCreatorSubmission(submissionId);
    setSubmission(res.submission);
    return res.submission;
  }, [submissionId]);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = `/login?next=/me/creator/work/${submissionId}`;
      return;
    }
    setLoading(true);
    load()
      .catch((e) => setError(e.message || "Could not load upload workspace"))
      .finally(() => setLoading(false));
  }, [load, submissionId]);

  useEffect(() => {
    const cancels = cancelRef.current;
    const previews = previewRef.current;
    return () => {
      Object.values(cancels).forEach((fn) => fn?.());
      Object.values(previews).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const setLocal = (shot, patch) => {
    setLocalClips((prev) => ({ ...prev, [shot]: { ...prev[shot], ...patch } }));
  };

  const clearLocal = (shot) => {
    setLocalClips((prev) => {
      if (prev[shot]?.previewUrl) URL.revokeObjectURL(prev[shot].previewUrl);
      delete previewRef.current[shot];
      const next = { ...prev };
      delete next[shot];
      return next;
    });
  };

  const onPickFile = async (itemKey, file, { semantic = false, hasExisting = false } = {}) => {
    if (!file) return;
    if (
      hasExisting &&
      !window.confirm("Replace the current video with this new file?")
    ) {
      return;
    }
    setError("");
    setMessage("");

    if (file.size > MAX_BYTES) {
      setLocal(itemKey, {
        phase: "failed",
        error: "Clip must be 200 MB or smaller. Keep clips short.",
      });
      return;
    }

    if (previewRef.current[itemKey]) {
      URL.revokeObjectURL(previewRef.current[itemKey]);
    }
    const previewUrl = URL.createObjectURL(file);
    previewRef.current[itemKey] = previewUrl;
    let wakeLock = null;
    try {
      if (navigator.wakeLock?.request) {
        wakeLock = await navigator.wakeLock.request("screen");
      }
    } catch {
      /* optional */
    }

    setLocal(itemKey, { phase: "preparing", progress: 0, previewUrl });
    try {
      const prep = await (semantic
        ? requestDeliverableUploadUrl(submissionId, itemKey, {
            name: file.name,
            type: file.type || "video/mp4",
            size: file.size,
          })
        : requestClipUploadUrl(submissionId, itemKey, {
        name: file.name,
        type: file.type || "video/mp4",
        size: file.size,
          }));

      setLocal(itemKey, { phase: "uploading", progress: 0, previewUrl });
      const { promise, cancel } = putFileWithProgress(prep.upload_url, file, {
        contentType: prep.content_type,
        onProgress: (pct) =>
          setLocal(itemKey, { phase: "uploading", progress: pct, previewUrl }),
      });
      cancelRef.current[itemKey] = cancel;
      await promise;
      delete cancelRef.current[itemKey];

      setLocal(itemKey, { phase: "preparing", progress: 100, previewUrl });
      const complete = semantic ? completeDeliverableUpload : completeClipUpload;
      const done = await complete(submissionId, itemKey, {
        s3_key: prep.s3_key,
        content_type: prep.content_type,
        byte_size: file.size,
        file_name: file.name,
      });
      setSubmission(done.submission);
      clearLocal(itemKey);
    } catch (e) {
      setLocal(itemKey, {
        phase: "failed",
        error: e.message || "Upload failed. Try again.",
        previewUrl,
      });
    } finally {
      try {
        await wakeLock?.release?.();
      } catch {
        /* ignore */
      }
    }
  };

  const onCancel = (itemKey) => {
    cancelRef.current[itemKey]?.();
    delete cancelRef.current[itemKey];
    clearLocal(itemKey);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const res = await submitCreatorSubmission(submissionId);
      setSubmission(res.submission);
      setMessage(res.message || "Submitted for review.");
    } catch (e) {
      if (handleCreatorGateError(e, `/me/creator/work/${submissionId}`)) return;
      setError(e.message || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CreatorLayout
        title="Upload clips"
        breadcrumbs={CREATOR_CRUMBS.upload("Upload clips")}
        backTo="/me/creator/work"
        contactNextPath={`/me/creator/work/${submissionId}`}
      >
        <TrainingLoading label="Loading clips..." />
      </CreatorLayout>
    );
  }

  if (!submission) {
    return (
      <CreatorLayout
        title="Upload clips"
        breadcrumbs={CREATOR_CRUMBS.upload("Upload clips")}
        backTo="/me/creator/work"
        contactNextPath="/me/creator/work"
      >
        <div className="training-empty">
          <p>{error || "Submission not found."}</p>
          <Link className="training-btn-primary" to="/me/creator/work">
            Back to my work
          </Link>
        </div>
      </CreatorLayout>
    );
  }

  const clipsByShot = Object.fromEntries(
    (submission.clips || []).map((c) => [c.shot_number, c])
  );
  const v2 = isV2Brief(submission);
  const progress = getSubmissionProgress(submission);
  const deliverableGroups = v2 ? groupV2Deliverables(submission) : [];
  const anyBusy = Object.values(localClips).some(
    (c) => c.phase === "preparing" || c.phase === "uploading"
  );
  const anyFailed = Object.values(localClips).some((c) => c.phase === "failed");
  const canSubmit = submission.can_submit && !anyBusy && !anyFailed && !submitting;
  const uploadsClosed = Boolean(submission.uploads_closed);
  const locked =
    ["submitted", "approved", "rejected"].includes(submission.status) || uploadsClosed;
  const crumbCode = submission.script?.code || "Upload clips";
  const crumbTitle = submission.script?.title || "";

  return (
    <CreatorLayout
      title={v2 ? "Upload performances" : "Upload clips"}
      breadcrumbs={CREATOR_CRUMBS.uploadWithTitle(crumbCode, crumbTitle)}
      backTo="/me/creator/work"
      contactNextPath={`/me/creator/work/${submissionId}`}
    >
      <section className="creator-hero">
        <p className="training-kicker">
          {submission.script.code} · {submission.payment_label} pending after approval
        </p>
        <h2 className="creator-hero__title">{submission.script.title}</h2>
        <p className="creator-tip">
          {v2
            ? "Record vertical 9:16. Upload Hook A, Hook B, Hook C and the shared body as four separate videos."
            : "Record vertical 9:16. Good light. Clear voice. Upload each shot separately."}
        </p>
        <div className="creator-progress-panel">
          <div className="creator-progress-panel__head">
            <p className="creator-progress-panel__count">
              <strong>
                {progress.ready}/{progress.needed || 0}
              </strong>{" "}
              {v2 ? "performances ready" : "clips ready"}
            </p>
            <CreatorStatusChip status={submission.status} />
          </div>
          <progress
            className="creator-progress"
            max={progress.needed || 1}
            value={progress.ready}
            aria-label={`${progress.ready} of ${progress.needed || 0} ready`}
          />
        </div>
      </section>

      {error ? <p className="creator-error">{error}</p> : null}
      {message ? <p className="creator-success">{message}</p> : null}
      {uploadsClosed ? (
        <div className="creator-brief-callout creator-brief-callout--avoid" role="status">
          <strong>This script is no longer available</strong>
          <p>
            {submission.script?.unavailable_message ||
              "The deadline has passed, so new uploads are closed."}
          </p>
        </div>
      ) : null}
      <CreatorDecisionBanner
        status={submission.status}
        note={submission.admin_note}
      />

      {v2 ? (
        <div className="creator-shot-list creator-shot-list--pad">
          {deliverableGroups.map((group) => {
            const item = group.upload;
            const ui = clipUiState(item, localClips[group.key]);
            const inputId = `deliverable-${group.key}`;
            const allowUpload =
              !locked &&
              (submission.status !== "retake" ||
                item?.status === "needs_retake" ||
                ui.phase === "failed" ||
                ui.phase === "empty");
            const previewUrl = localClips[group.key]?.previewUrl;

            return (
              <article key={group.key} className="training-card creator-shot-card creator-v2-upload">
                <div className="creator-shot-card__head">
                  <div>
                    <span className="creator-shot-card__num">{group.title}</span>
                    <span className="creator-v2-upload__type">
                      {group.kind === "hook" ? "Hook performance" : "Shared performance"}
                    </span>
                  </div>
                  {ui.phase === "empty" ? (
                    <span className="creator-chip creator-chip--neutral">Not uploaded</span>
                  ) : ui.phase === "preparing" ? (
                    <span className="creator-chip creator-chip--orange">Preparing...</span>
                  ) : ui.phase === "uploading" ? (
                    <span className="creator-chip creator-chip--orange">
                      Uploading {ui.progress || 0}%
                    </span>
                  ) : ui.phase === "failed" ? (
                    <span className="creator-chip creator-chip--red">Upload failed</span>
                  ) : (
                    <CreatorStatusChip status={ui.phase} />
                  )}
                </div>

                {group.kind === "hook" ? (
                  <div className="creator-say-panel">
                    <p className="creator-brief-say-label">Exact spoken hook</p>
                    <p className="creator-shot-card__line">&ldquo;{group.brief.spoken_hook}&rdquo;</p>
                  </div>
                ) : (
                  <div className="creator-say-panel">
                    <p className="creator-brief-say-label">Say this (shared body)</p>
                    <p className="creator-shot-card__line">{group.brief.creator_wording}</p>
                  </div>
                )}
                {(() => {
                  const cue =
                    group.kind === "hook"
                      ? [group.brief.opening_frame, group.brief.creator_action, group.brief.camera]
                          .filter(Boolean)
                          .join(" · ")
                      : group.brief.length_specs;
                  return cue ? <p className="creator-shot-card__cue">{cue}</p> : null;
                })()}

                {ui.phase === "needs_retake" && (ui.note || item?.retake_note) ? (
                  <p className="creator-work-card__note">
                    <strong>Retake note:</strong> {ui.note || item.retake_note}
                  </p>
                ) : null}
                {ui.phase === "uploading" ? (
                  <div
                    className="creator-upload-bar"
                    role="progressbar"
                    aria-label={`${group.title} upload`}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={ui.progress || 0}
                  >
                    <span style={{ width: `${ui.progress || 0}%` }} />
                  </div>
                ) : null}
                {ui.phase === "failed" ? <p className="creator-error">{ui.error}</p> : null}

                {previewUrl || item?.play_url ? (
                  <div className="creator-clip-player-wrap">
                    <p className="creator-clip-player-label">
                      {previewUrl ? "Selected video preview" : "Your uploaded video"}
                    </p>
                    <video
                      key={previewUrl || item.play_url}
                      className="creator-clip-player"
                      src={previewUrl || item.play_url}
                      controls
                      playsInline
                      preload="metadata"
                    />
                  </div>
                ) : null}

                {!locked && allowUpload ? (
                  <div className="creator-shot-card__actions creator-shot-card__actions--single">
                    {ui.phase === "uploading" || ui.phase === "preparing" ? (
                      <button
                        type="button"
                        className="training-btn-secondary"
                        onClick={() => onCancel(group.key)}
                      >
                        Cancel
                      </button>
                    ) : (
                      <>
                        <input
                          id={inputId}
                          className="creator-file-input"
                          type="file"
                          accept="video/mp4,video/quicktime,video/webm,video/*"
                          capture="environment"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            onPickFile(group.key, file, {
                              semantic: true,
                              hasExisting: Boolean(item?.has_file),
                            });
                          }}
                        />
                        <label
                          htmlFor={inputId}
                          className="training-btn-primary creator-file-label"
                        >
                          {item?.has_file
                            ? "Replace video"
                            : ui.phase === "failed"
                              ? "Try again"
                              : "Choose video"}
                        </label>
                      </>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
      <div className="creator-shot-list creator-shot-list--pad">
        {submission.shots.map((shot) => {
          const clip = clipsByShot[shot.shot_number];
          const ui = clipUiState(clip, localClips[shot.shot_number]);
          const inputId = `clip-${shot.shot_number}`;
          const allowUpload =
            !locked &&
            (submission.status !== "retake" ||
              clip?.status === "needs_retake" ||
              ui.phase === "failed" ||
              ui.phase === "empty");

          return (
            <article key={shot.shot_number} className="training-card creator-shot-card">
              <div className="creator-shot-card__head">
                <span className="creator-shot-card__num">Shot {shot.shot_number}</span>
                {ui.phase === "empty" ? null : ui.phase === "preparing" ? (
                  <span className="creator-chip creator-chip--orange">Preparing…</span>
                ) : ui.phase === "uploading" ? (
                  <span className="creator-chip creator-chip--orange">
                    Uploading {ui.progress || 0}%
                  </span>
                ) : ui.phase === "failed" ? (
                  <span className="creator-chip creator-chip--red">Upload failed</span>
                ) : (
                  <CreatorStatusChip
                    status={
                      ui.phase === "needs_retake"
                        ? "needs_retake"
                        : ui.phase === "accepted"
                          ? "accepted"
                          : "uploaded"
                    }
                  />
                )}
              </div>
              <div className="creator-say-panel">
                <p className="creator-brief-say-label">Say this</p>
                <p className="creator-shot-card__line">&ldquo;{shot.exact_sentence}&rdquo;</p>
              </div>
              <p className="creator-shot-card__cue">{formatClipCue(shot)}</p>
              {shot.do_this ? (
                <p className="creator-shot-card__prop"><strong>Do:</strong> {shot.do_this}</p>
              ) : null}
              {(shot.preview_image_urls || []).length > 0 ? (
                <div className="creator-brief-previews">
                  <p className="creator-brief-say-label">Look reference (strict)

</p>
                  <div className="creator-brief-preview-grid creator-brief-preview-grid--compact">
                    {shot.preview_image_urls.slice(0, 4).map((src, idx) => (
                      <div key={`${shot.shot_number}-ref-${idx}`} className="creator-brief-preview">
                        <img src={src} alt={`Shot ${shot.shot_number} reference ${idx + 1}`} loading="lazy" />
                        <span className="creator-brief-preview__cap">Ref {idx + 1}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {ui.phase === "needs_retake" && (ui.note || clip?.retake_note) ? (
                <p className="creator-work-card__note">{ui.note || clip.retake_note}</p>
              ) : null}
              {ui.phase === "uploading" ? (
                <div className="creator-upload-bar" aria-hidden="true">
                  <span style={{ width: `${ui.progress || 0}%` }} />
                </div>
              ) : null}
              {ui.phase === "failed" ? (
                <p className="creator-error">{ui.error}</p>
              ) : null}

              {localClips[shot.shot_number]?.previewUrl ||
              (clip?.play_url &&
                ui.phase !== "empty" &&
                ui.phase !== "failed") ? (
                <div className="creator-clip-player-wrap">
                  <div className="creator-clip-player-head">
                    <p className="creator-clip-player-label">
                      {localClips[shot.shot_number]?.previewUrl
                        ? "Selected video preview"
                        : "Your clip"}
                    </p>
                    <button
                      type="button"
                      className="creator-clip-play-btn"
                      onClick={(e) => {
                        const wrap = e.currentTarget.closest(".creator-clip-player-wrap");
                        const video = wrap?.querySelector("video");
                        if (!video) return;
                        if (video.paused) video.play().catch(() => {});
                        else video.pause();
                      }}
                    >
                      Play / pause
                    </button>
                  </div>
                  <video
                    key={localClips[shot.shot_number]?.previewUrl || clip?.play_url}
                    className="creator-clip-player"
                    src={localClips[shot.shot_number]?.previewUrl || clip?.play_url}
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              ) : null}

              {!locked && allowUpload ? (
                <div className="creator-shot-card__actions">
                  {ui.phase === "uploading" ? (
                    <button
                      type="button"
                      className="training-btn-secondary"
                      onClick={() => onCancel(shot.shot_number)}
                    >
                      Cancel
                    </button>
                  ) : (
                    <>
                      <input
                        id={inputId}
                        className="creator-file-input"
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/*"
                        capture="environment"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          onPickFile(shot.shot_number, f, {
                            hasExisting: Boolean(clip?.has_file),
                          });
                        }}
                      />
                      <label htmlFor={inputId} className="training-btn-primary creator-file-label">
                        {ui.phase === "uploaded" || ui.phase === "needs_retake"
                          ? "Replace video"
                          : ui.phase === "failed"
                            ? "Try again"
                            : "Choose video"}
                      </label>
                    </>
                  )}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
      )}

      {!locked ? (
        <div className="creator-sticky-actions">
          <p className="creator-sticky-actions__meta">
            {progress.label}
            {anyBusy ? " · Upload in progress" : ""}
          </p>
          <button
            type="button"
            className="training-btn-primary creator-sticky-actions__btn"
            disabled={!canSubmit}
            onClick={onSubmit}
          >
            {submitting ? "Submitting..." : "Submit for review"}
          </button>
          {!submission.can_submit ? (
            <p className="creator-sticky-actions__hint">Upload all required clips first.</p>
          ) : null}
        </div>
      ) : (
        <div className="creator-sticky-actions">
          <Link className="training-btn-secondary creator-sticky-actions__btn" to="/me/creator/work">
            Back to my work
          </Link>
          {submission.status === "approved" ? (
            <Link className="training-btn-primary creator-sticky-actions__btn" to="/me/creator/payments">
              View payments
            </Link>
          ) : null}
        </div>
      )}
    </CreatorLayout>
  );
}
